/**
 * Auto-Recharge Configuration Dialog
 * Allows users to configure automatic credit recharge when balance falls below threshold
 */

import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { useApi } from '../../../hooks/useApi';

// Predefined threshold options
const THRESHOLD_OPTIONS = [5, 10, 25, 50];

// Predefined recharge amount options
const RECHARGE_OPTIONS = [25, 50, 100, 200];

const MIN_RECHARGE = 10;

/**
 * AutoRechargeDialog Component
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Callback when dialog is closed
 * @param {Object} props.currentConfig - Current auto-recharge configuration
 * @param {boolean} props.hasPaymentMethod - Whether user has a payment method
 */
function AutoRechargeDialog({ open, onClose, currentConfig = {}, hasPaymentMethod = false }) {
  const api = useApi();
  const queryClient = useQueryClient();

  const [enabled, setEnabled] = useState(currentConfig.enabled || false);
  const [threshold, setThreshold] = useState(currentConfig.threshold || 10);
  const [amount, setAmount] = useState(currentConfig.amount || 50);
  const [customThreshold, setCustomThreshold] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [useCustomThreshold, setUseCustomThreshold] = useState(false);
  const [useCustomAmount, setUseCustomAmount] = useState(false);
  const [error, setError] = useState(null);

  // Reset form when dialog opens with new config
  useEffect(() => {
    if (open) {
      setEnabled(currentConfig.enabled || false);
      setThreshold(currentConfig.threshold || 10);
      setAmount(currentConfig.amount || 50);
      setCustomThreshold('');
      setCustomAmount('');
      setUseCustomThreshold(false);
      setUseCustomAmount(false);
      setError(null);
    }
  }, [open, currentConfig]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (config) => {
      return api.post('/api/billing/auto-recharge', config);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['billing']);
      onClose(true);
    },
    onError: (err) => {
      setError(err.message || 'Failed to save auto-recharge settings');
    },
  });

  const handleSave = () => {
    const finalThreshold = useCustomThreshold ? parseFloat(customThreshold) : threshold;
    const finalAmount = useCustomAmount ? parseFloat(customAmount) : amount;

    if (enabled) {
      if (isNaN(finalThreshold) || finalThreshold < 0) {
        setError('Please enter a valid threshold amount');
        return;
      }

      if (isNaN(finalAmount) || finalAmount < MIN_RECHARGE) {
        setError(`Recharge amount must be at least $${MIN_RECHARGE}`);
        return;
      }

      if (!hasPaymentMethod) {
        setError('Please add a payment method before enabling auto-recharge');
        return;
      }
    }

    saveMutation.mutate({
      enabled,
      threshold: enabled ? finalThreshold : 0,
      amount: enabled ? finalAmount : 0,
    });
  };

  const handleClose = () => {
    if (!saveMutation.isPending) {
      setError(null);
      onClose();
    }
  };

  const finalThreshold = useCustomThreshold ? parseFloat(customThreshold) || 0 : threshold;
  const finalAmount = useCustomAmount ? parseFloat(customAmount) || 0 : amount;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: { sx: { bgcolor: 'background.paper' } }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutorenewIcon />
          Configure Auto-Recharge
        </Box>
        <IconButton onClick={handleClose} size="small" disabled={saveMutation.isPending}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            mb: 3
          }}>
          Automatically add credits when your balance falls below a threshold.
        </Typography>

        {/* Enable Toggle */}
        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              onChange={(e) => {
                setEnabled(e.target.checked);
                setError(null);
              }}
            />
          }
          label="Enable auto-recharge"
          sx={{ mb: 3 }}
        />

        {enabled && (
          <>
            {/* Threshold Selection */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Recharge when balance falls below
              </Typography>
              <TextField
                select
                fullWidth
                value={useCustomThreshold ? 'custom' : threshold}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === 'custom') {
                    setUseCustomThreshold(true);
                  } else {
                    setUseCustomThreshold(false);
                    setThreshold(value);
                    setCustomThreshold('');
                  }
                  setError(null);
                }}
              >
                {THRESHOLD_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    ${option}
                  </MenuItem>
                ))}
                <MenuItem value="custom">Custom amount...</MenuItem>
              </TextField>
              {useCustomThreshold && (
                <TextField
                  fullWidth
                  type="number"
                  value={customThreshold}
                  onChange={(e) => {
                    setCustomThreshold(e.target.value);
                    setError(null);
                  }}
                  placeholder="Enter threshold amount"
                  sx={{ mt: 1 }}
                  slotProps={{
                    input: {
                      startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                    },

                    htmlInput: { min: 0, step: 1 }
                  }} />
              )}
            </Box>

            {/* Recharge Amount Selection */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Amount to recharge
              </Typography>
              <TextField
                select
                fullWidth
                value={useCustomAmount ? 'custom' : amount}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === 'custom') {
                    setUseCustomAmount(true);
                  } else {
                    setUseCustomAmount(false);
                    setAmount(value);
                    setCustomAmount('');
                  }
                  setError(null);
                }}
              >
                {RECHARGE_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    ${option}
                  </MenuItem>
                ))}
                <MenuItem value="custom">Custom amount...</MenuItem>
              </TextField>
              {useCustomAmount && (
                <TextField
                  fullWidth
                  type="number"
                  value={customAmount}
                  onChange={(e) => {
                    setCustomAmount(e.target.value);
                    setError(null);
                  }}
                  placeholder={`Minimum $${MIN_RECHARGE}`}
                  sx={{ mt: 1 }}
                  slotProps={{
                    input: {
                      startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                    },

                    htmlInput: { min: MIN_RECHARGE, step: 1 }
                  }} />
              )}
            </Box>

            {/* Summary */}
            <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="body2">
                When your balance drops below <strong>${finalThreshold.toFixed(2)}</strong>,
                we will automatically charge your default payment method{' '}
                <strong>${finalAmount.toFixed(2)}</strong>.
              </Typography>
            </Box>

            {!hasPaymentMethod && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Please add a payment method before enabling auto-recharge.
              </Alert>
            )}
          </>
        )}

        {/* Error */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose} disabled={saveMutation.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saveMutation.isPending || (enabled && !hasPaymentMethod)}
          startIcon={saveMutation.isPending ? <CircularProgress size={20} /> : null}
        >
          {saveMutation.isPending ? 'Saving...' : 'Save Settings'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AutoRechargeDialog;
